<template>
  <div class="info-section">
    <h5>{{ title }}</h5>
    <div class="info-grid">
      <div
        v-for="(item, index) in formattedItems"
        :key="index"
        class="info-item"
      >
        <span class="label">{{ item.label }}:</span>
        <span
          class="value"
          :class="item.valueClass"
          v-html="item.formattedValue"
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CycleInfoGrid',
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true,
      validator: (items) => {
        return items.every(item =>
          typeof item === 'object' &&
          item.label &&
          'value' in item
        );
      }
    }
  },
  computed: {
    // Memoize formatted items to avoid recalculation on every render
    formattedItems() {
      return this.items.map(item => ({
        ...item,
        formattedValue: this.formatValue(item.value, item.type)
      }));
    }
  },
  methods: {
    formatValue(value, type) {
      if (value === null || value === undefined) {
        return 'N/A';
      }

      switch (type) {
        case 'temperature':
          return `${value}°F`;
        case 'humidity':
          return `${value}%`;
        case 'pressure':
          return `${value} Pa`;
        case 'duration':
          return this.formatDuration(value);
        case 'datetime':
          return this.formatDateTime(value);
        case 'badge':
          return value; // Badge content is already formatted in the class
        case 'status':
          return value; // Status content is already formatted in the class
        default:
          return value;
      }
    },
    formatDuration(seconds) {
      if (!seconds) return 'N/A';
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.info-section {
  margin-bottom: 24px;
  background: rgb(24, 32, 64);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgb(58, 74, 107);

  h5 {
    margin: 0 0 16px 0;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    border-bottom: 2px solid #4a9eff;
    padding-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '📊';
      font-size: 14px;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 16px;

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      background: rgb(32, 40, 80);
      border-radius: 8px;
      border: 1px solid rgb(58, 74, 107);
      min-height: 52px;
      transition: all 0.2s ease;
      overflow: hidden;

      &:hover {
        background: rgb(42, 58, 107);
        border-color: #4a9eff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }

      .label {
        font-weight: 600;
        color: #ffffff;
        font-size: 13px;
        flex-shrink: 1;
        margin-right: 16px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        max-width: 65%;
      }

      .value {
        font-weight: 700;
        color: #ffffff;
        font-size: 14px;
        text-align: right;
        flex-shrink: 0;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;

        &.mode-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 2px solid transparent;
          white-space: nowrap;
          word-break: normal;

          &.cool {
            background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
            color: #1976d2;
            border-color: #1976d2;
          }

          &.heat {
            background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
            color: #d32f2f;
            border-color: #d32f2f;
          }

          &.fan {
            background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
            color: #7b1fa2;
            border-color: #7b1fa2;
          }
        }

        &.status-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 2px solid transparent;
          white-space: nowrap;
          word-break: normal;

          &.valid {
            background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
            color: #2e7d32;
            border-color: #2e7d32;
          }

          &.invalid {
            background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
            color: #d32f2f;
            border-color: #d32f2f;
          }

          &.not-calculated {
            background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
            color: #f57c00;
            border-color: #f57c00;
          }
        }
      }
    }
  }
}
</style>
